import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { v4 as uuid }                                            from 'uuid';
import { Button, Checkbox, Form, Icon, Input, Label, Loader, Menu, Message } from 'semantic-ui-react';
import type { DropdownItemProps, SemanticICONS }                 from 'semantic-ui-react';
import { BasicDialog }                                           from '@src/views/components/modals/BasicDialog';
import { BasicDialogContainer }                                  from '@src/views/components/modals/BasicDialogContainer';
import { Align, SemanticUIColor }                                from '@src/types';
import { useCentralCalls }                                       from '@src/rest/CentralCalls';
import { useSystemToasts }                                       from '@src/hooks';

/** Built-in agent with no instructions/tools (seeded by Migration007TNGDefaultAIAgent); always available, never editable. */
export const DEFAULT_AI_AGENT_ID = 'f8514cd2-87b4-4351-acb5-7e8f9becec49';

/**
 * Manages AI agents, instructions, anchors and tools.
 * Anchors are AIInstruction rows with InstructionType = 'Anchor'; they share the table and the AIAgentInstruction links.
 *
 * Reads everything in one call to tng.sp_get_AIManagement; writes go through the generic
 * RunInsert / RunUpdate endpoints (DbSets AIAgents, AIInstructions, AITools, AIAgentInstructions, AIAgentTools).
 * Agent ↔ instruction/tool links are composite-key rows, so unlinking uses RunDelete with keys.
 */

export type AIManagementTab = 'agent' | 'instruction' | 'anchor' | 'tool';

/** tng.AIInstruction.InstructionType values; compared case-insensitively since the column is free text. */
const INSTRUCTION_TYPE = { instruction: 'Instruction', anchor: 'Anchor' } as const;

const isAnchor = ( instruction: { instructionType?: string | null } ) =>
	( instruction.instructionType ?? '' ).toLowerCase() === INSTRUCTION_TYPE.anchor.toLowerCase();

export interface AIAgentRecord {
	id: string;
	name: string;
	description: string | null;
	defaultAIModelId: string;
	defaultAIModelName: string | null;
	active: boolean;
	updatedUtc: string;
	instructionIds: string[];
	/** Split out of the AIAgentInstruction links by instruction type. */
	anchorIds: string[];
	toolIds: string[];
}

export interface AIInstructionRecord {
	id: string;
	name: string;
	instructions: string;
	description: string | null;
	instructionType: string;
	active: boolean;
	updatedUtc: string;
	agentCount: number;
}

export interface AIToolRecord {
	id: string;
	name: string;
	description: string | null;
	toolType: string | null;
	active: boolean;
	updatedUtc: string;
	agentCount: number;
}

export interface AIModelRecord {
	id: string;
	name: string;
	provider: string;
	modelIdentifier: string;
	description: string | null;
	active: boolean;
}

interface ListRow {
	id: string;
	name: string;
	meta: string;
	active: boolean;
}

/**
 * The current user's consultant record, resolved by the stored procedure from AccountId.
 * New agents are saved against it; it is never shown or edited.
 */
interface AIManagementContext {
	consultantId: string | null;
}

interface AIManagementData {
	context: AIManagementContext;
	agents: AIAgentRecord[];
	instructions: AIInstructionRecord[];
	anchors: AIInstructionRecord[];
	tools: AIToolRecord[];
	models: AIModelRecord[];
}

/** One editable shape for every tab; each tab only reads/writes the fields it uses. Anchors use the instruction fields. */
interface ItemDraft {
	id: string | null;
	name: string;
	description: string;
	active: boolean;
	defaultAIModelId: string;
	instructionIds: string[];
	anchorIds: string[];
	toolIds: string[];
	instructions: string;
	toolType: string;
}

interface AIAgentManagementProps {
	/**
	 * The client of the console this is opened from. Fixed for the life of the component: agents and
	 * instructions are listed and created for this client only, and it is never shown or edited.
	 */
	clientId?: string;
	initialTab?: AIManagementTab;
	/** Select this agent once data loads. */
	initialAgentId?: string;
	/** Open with a blank form for a new item on `initialTab`. */
	startWithNew?: boolean;
	onClose: () => void;
	/** Called after any successful save so callers can refresh their own agent lists. */
	onSaved?: () => void;
}

const EMPTY_CONTEXT: AIManagementContext = { consultantId: null };

const EMPTY_DATA: AIManagementData = { context: EMPTY_CONTEXT, agents: [], instructions: [], anchors: [], tools: [], models: [] };

const TAB_LABELS: Record<AIManagementTab, { plural: string; singular: string; icon: SemanticICONS }> = {
	agent:       { plural: 'Agents',       singular: 'Agent',       icon: 'magic' as SemanticICONS },
	instruction: { plural: 'Instructions', singular: 'Instruction', icon: 'sticky note outline' as SemanticICONS },
	anchor:      { plural: 'Anchors',      singular: 'Anchor',      icon: 'anchor' as SemanticICONS },
	tool:        { plural: 'Tools',        singular: 'Tool',        icon: 'wrench' as SemanticICONS },
};

const MAX_NAME_LENGTH = 100;

const toArray = <T, >( value: unknown ): T[] => ( Array.isArray( value ) ? value as T[] : [] );

/** Active defaults to true; only an explicit false (or 0 / "false") marks a record inactive. */
const isActiveValue = ( value: unknown ): boolean =>
	!( value === false || value === 0 || String( value ).toLowerCase() === 'false' || value === '0' );

const withActive = <T extends { active: boolean }>( records: T[] ): T[] =>
	records.map( record => ( { ...record, active: isActiveValue( record.active ) } ) );

const normalizeData = ( response: unknown ): AIManagementData => {
	const raw = typeof response === 'string' ? JSON.parse( response ) : response;
	const payload = ( Array.isArray( raw ) ? raw[ 0 ] : raw ) ?? {};

	const allInstructions = withActive( toArray<AIInstructionRecord>( payload.instructions ) );
	const anchorIds       = new Set( allInstructions.filter( isAnchor ).map( i => i.id ) );

	return {
		context:      { ...EMPTY_CONTEXT, ...( payload.context ?? {} ) },
		agents:       withActive( toArray<AIAgentRecord>( payload.agents ) ).filter( agent => agent.id !== DEFAULT_AI_AGENT_ID ).map( agent => {
			const linkedIds = toArray<string>( agent.instructionIds );
			return {
				...agent,
				instructionIds: linkedIds.filter( id => !anchorIds.has( id ) ),
				anchorIds:      linkedIds.filter( id => anchorIds.has( id ) ),
				toolIds:        toArray<string>( agent.toolIds ),
			};
		} ),
		instructions: allInstructions.filter( i => !isAnchor( i ) ),
		anchors:      allInstructions.filter( isAnchor ),
		tools:        withActive( toArray<AIToolRecord>( payload.tools ) ),
		models:       withActive( toArray<AIModelRecord>( payload.models ) ),
	};
};

const emptyDraft = ( defaultModelId = '' ): ItemDraft => ( {
	id:               null,
	name:             '',
	description:      '',
	active:           true,
	defaultAIModelId: defaultModelId,
	instructionIds:   [],
	anchorIds:        [],
	toolIds:          [],
	instructions:     '',
	toolType:         '',
} );

const draftFromAgent = ( agent: AIAgentRecord ): ItemDraft => ( {
	...emptyDraft( agent.defaultAIModelId ),
	id:             agent.id,
	name:           agent.name,
	description:    agent.description ?? '',
	active:         agent.active,
	instructionIds: [ ...agent.instructionIds ],
	anchorIds:      [ ...agent.anchorIds ],
	toolIds:        [ ...agent.toolIds ],
} );

const draftFromInstruction = ( instruction: AIInstructionRecord ): ItemDraft => ( {
	...emptyDraft(),
	id:           instruction.id,
	name:         instruction.name,
	description:  instruction.description ?? '',
	active:       instruction.active,
	instructions: instruction.instructions,
} );

const draftFromTool = ( tool: AIToolRecord ): ItemDraft => ( {
	...emptyDraft(),
	id:          tool.id,
	name:        tool.name,
	description: tool.description ?? '',
	active:      tool.active,
	toolType:    tool.toolType ?? '',
} );

const nullIfBlank = ( value: string ): string | null => value.trim() || null;

const sameIds = ( a: string[], b: string[] ) => a.length === b.length && a.every( id => b.includes( id ) );

const isSameDraft = ( a: ItemDraft | null, b: ItemDraft | null ): boolean => {
	if( !a || !b ) return a === b;
	return a.name === b.name
		&& a.description === b.description
		&& a.active === b.active
		&& a.defaultAIModelId === b.defaultAIModelId
		&& a.instructions === b.instructions
		&& a.toolType === b.toolType
		&& sameIds( a.instructionIds, b.instructionIds )
		&& sameIds( a.anchorIds, b.anchorIds )
		&& sameIds( a.toolIds, b.toolIds );
};

const AIAgentManagement: FC<AIAgentManagementProps> = ( {
	clientId,
	initialTab = 'agent',
	initialAgentId,
	startWithNew = false,
	onClose,
	onSaved,
} ) => {

	const { executeStoredProcedure, RunInsert, RunUpdate, RunDelete } = useCentralCalls();
	const toastActions = useSystemToasts();

	const [ data, setData ]                 = useState<AIManagementData>( EMPTY_DATA ),
	      [ isLoading, setIsLoading ]       = useState( true ),
	      [ loadError, setLoadError ]       = useState( '' ),
	      [ tab, setTab ]                   = useState<AIManagementTab>( initialTab ),
	      [ search, setSearch ]             = useState( '' ),
	      [ selectedId, setSelectedId ]     = useState<string | null>( null ),
	      [ baseline, setBaseline ]         = useState<ItemDraft | null>( null ),
	      [ draft, setDraft ]               = useState<ItemDraft | null>( null ),
	      [ isSaving, setIsSaving ]         = useState( false ),
	      [ formError, setFormError ]       = useState( '' );

	const isDirty = !isSameDraft( draft, baseline );

	// New agents are saved against the console's client + the user's consultant and new instructions against the client;
	// without them the new item would not appear in this list.
	const scopeErrorFor = ( kind: AIManagementTab, context: AIManagementContext ): string => {
		if( kind === 'tool' ) return '';
		if( !clientId ) return `Select a client on the dashboard before adding ${ TAB_LABELS[ kind ].plural.toLowerCase() }.`;
		if( kind === 'agent' && !context.consultantId ) return 'Your account is not set up as a consultant, so you cannot add agents.';
		return '';
	};
	const scopeError = scopeErrorFor( tab, data.context );
	const canCreate  = !scopeError;
	const isNew   = !!draft && !draft.id;

	const activeModels = useMemo( () => data.models.filter( m => m.active ), [ data.models ] );

	const draftForRecord = useCallback( ( kind: AIManagementTab, id: string, source: AIManagementData ): ItemDraft | null => {
		if( kind === 'agent' ){
			const agent = source.agents.find( a => a.id === id );
			return agent ? draftFromAgent( agent ) : null;
		}
		if( kind === 'instruction' || kind === 'anchor' ){
			const instruction = ( kind === 'anchor' ? source.anchors : source.instructions ).find( i => i.id === id );
			return instruction ? draftFromInstruction( instruction ) : null;
		}
		const tool = source.tools.find( t => t.id === id );
		return tool ? draftFromTool( tool ) : null;
	}, [] );

	const recordsFor = ( kind: AIManagementTab, source: AIManagementData ): { id: string }[] =>
		kind === 'agent' ? source.agents
		: kind === 'instruction' ? source.instructions
		: kind === 'anchor' ? source.anchors
		: source.tools;

	const openItem = ( kind: AIManagementTab, id: string | null, source: AIManagementData ) => {
		const next = id ? draftForRecord( kind, id, source ) : null;
		setSelectedId( next ? id : null );
		setBaseline( next );
		setDraft( next );
		setFormError( '' );
	};

	const openNew = ( kind: AIManagementTab, source: AIManagementData ) => {
		const next = emptyDraft( kind === 'agent' ? ( source.models.find( m => m.active )?.id ?? '' ) : '' );
		setSelectedId( null );
		setBaseline( next );
		setDraft( next );
		setFormError( '' );
	};

	const loadData = useCallback( async (): Promise<AIManagementData | null> => {
		setIsLoading( true );
		setLoadError( '' );
		try{
			// AccountId is added server-side by ExecuteStoredProcedure.
			const response = await executeStoredProcedure( 'tng.sp_get_AIManagement', { ClientId: clientId ?? null } );
			const next = normalizeData( response );
			setData( next );
			return next;
		} catch( e ) {
			console.error( 'Failed to load AI management data', e );
			setLoadError( 'Agents, instructions, anchors and tools could not be loaded.' );
			return null;
		} finally {
			setIsLoading( false );
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [] );

	useEffect( () => {
		( async () => {
			const loaded = await loadData();
			if( !loaded ) return;

			if( startWithNew && !scopeErrorFor( initialTab, loaded.context ) ){
				openNew( initialTab, loaded );
			} else if( initialTab === 'agent' && initialAgentId && loaded.agents.some( a => a.id === initialAgentId ) ){
				openItem( 'agent', initialAgentId, loaded );
			} else {
				openItem( initialTab, recordsFor( initialTab, loaded )[ 0 ]?.id ?? null, loaded );
			}
		} )();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [] );

	const confirmDiscard = () => !isDirty || window.confirm( 'You have unsaved changes. Discard them?' );

	const handleTabChange = ( next: AIManagementTab ) => {
		if( next === tab || !confirmDiscard() ) return;
		setTab( next );
		setSearch( '' );
		openItem( next, recordsFor( next, data )[ 0 ]?.id ?? null, data );
	};

	const handleSelect = ( id: string ) => {
		if( id === selectedId || !confirmDiscard() ) return;
		openItem( tab, id, data );
	};

	const handleNew = () => {
		if( !canCreate || !confirmDiscard() ) return;
		openNew( tab, data );
	};

	const handleClose = () => {
		if( !confirmDiscard() ) return;
		onClose();
	};

	const updateDraft = ( patch: Partial<ItemDraft> ) => {
		setDraft( prev => ( prev ? { ...prev, ...patch } : prev ) );
		if( formError ) setFormError( '' );
	};

	// ── Validation ─────────────────────────────────────────────────────────────

	const validate = ( item: ItemDraft ): string => {
		const name = item.name.trim();
		if( !item.id && scopeError ) return scopeError;
		if( !name ) return 'Name is required.';
		if( name.length > MAX_NAME_LENGTH ) return `Name must be ${ MAX_NAME_LENGTH } characters or fewer.`;
		if( tab === 'agent' && !item.defaultAIModelId ) return 'Select a default model.';
		if( tab === 'instruction' && !item.instructions.trim() ) return 'Instruction text is required.';
		if( tab === 'anchor' && !item.instructions.trim() ) return 'Anchor text is required.';
		if( tab === 'tool' && item.toolType.trim().length > MAX_NAME_LENGTH ) return `Tool type must be ${ MAX_NAME_LENGTH } characters or fewer.`;
		return '';
	};

	// ── Save ───────────────────────────────────────────────────────────────────

	/** Adds/removes join rows so the agent's links match `nextIds`. */
	const syncLinks = async ( table: 'AIAgentInstructions' | 'AIAgentTools', linkKey: 'AIInstructionId' | 'AIToolId',
	                          agentId: string, previousIds: string[], nextIds: string[] ) => {
		const toAdd    = nextIds.filter( id => !previousIds.includes( id ) );
		const toRemove = previousIds.filter( id => !nextIds.includes( id ) );

		for( const id of toAdd ){
			await RunInsert( table, { AIAgentId: agentId, [ linkKey ]: id } );
		}
		for( const id of toRemove ){
			await RunDelete( table, null, { AIAgentId: agentId, [ linkKey ]: id } );
		}
	};

	/**
	 * Inserts a new row as active. EF treats Active=false as "unset" (the column defaults to true),
	 * so an item created inactive is switched off with a follow-up update.
	 */
	const insertItem = async ( table: 'AIAgents' | 'AIInstructions' | 'AITools', id: string, fields: object, active: boolean ) => {
		await RunInsert( table, { Id: id, ...fields, Active: true } );
		if( !active ) await RunUpdate( table, id, { Active: false } );
	};

	const saveDraft = async ( item: ItemDraft ): Promise<string> => {
		const id = item.id ?? uuid();
		const common = {
			Name:        item.name.trim(),
			Description: nullIfBlank( item.description ),
		};

		if( tab === 'agent' ){
			const fields = { ...common, DefaultAIModelId: item.defaultAIModelId };
			// Client / consultant are set once on insert from the console's context and never updated.
			if( item.id ) await RunUpdate( 'AIAgents', id, { ...fields, Active: item.active } );
			else          await insertItem( 'AIAgents', id, { ...fields, ClientId: clientId, ConsultantId: data.context.consultantId }, item.active );

			// Instructions and anchors are both AIAgentInstruction links.
			const previous = item.id ? data.agents.find( a => a.id === item.id ) : undefined;
			await syncLinks( 'AIAgentInstructions', 'AIInstructionId', id,
				[ ...( previous?.instructionIds ?? [] ), ...( previous?.anchorIds ?? [] ) ],
				[ ...item.instructionIds, ...item.anchorIds ] );
			await syncLinks( 'AIAgentTools', 'AIToolId', id, previous?.toolIds ?? [], item.toolIds );
			return id;
		}

		if( tab === 'instruction' || tab === 'anchor' ){
			const fields = { ...common, Instructions: item.instructions.trim() };
			// The type is set once on insert; an instruction never turns into an anchor or back.
			const instructionType = tab === 'anchor' ? INSTRUCTION_TYPE.anchor : INSTRUCTION_TYPE.instruction;
			if( item.id ) await RunUpdate( 'AIInstructions', id, { ...fields, Active: item.active } );
			else          await insertItem( 'AIInstructions', id, { ...fields, ClientId: clientId, InstructionType: instructionType }, item.active );
			return id;
		}

		const fields = { ...common, ToolType: nullIfBlank( item.toolType ) };
		if( item.id ) await RunUpdate( 'AITools', id, { ...fields, Active: item.active } );
		else          await insertItem( 'AITools', id, fields, item.active );
		return id;
	};

	const handleSave = async () => {
		if( !draft || isSaving ) return;

		const validationError = validate( draft );
		if( validationError ){
			setFormError( validationError );
			return;
		}

		setIsSaving( true );
		setFormError( '' );

		let savedId: string | null = null;
		try{
			savedId = await saveDraft( draft );
			toastActions.success( `${ TAB_LABELS[ tab ].singular } saved` );
			onSaved?.();
		} catch( e ) {
			console.error( 'Failed to save AI management item', e );
			setFormError( `The ${ TAB_LABELS[ tab ].singular.toLowerCase() } could not be saved. Reload to see what was stored, then try again.` );
		} finally {
			// Always reload: a multi-step agent save may have partially applied before failing.
			const reloaded = await loadData();
			setIsSaving( false );
			if( reloaded && savedId ) openItem( tab, savedId, reloaded );
		}
	};

	const handleReset = () => {
		setDraft( baseline );
		setFormError( '' );
	};

	// ── Lookups ────────────────────────────────────────────────────────────────

	const modelOptions: DropdownItemProps[] = useMemo( () => data.models
		.filter( m => m.active || m.id === draft?.defaultAIModelId )
		.map( m => ( {
			key:         m.id,
			value:       m.id,
			text:        m.active ? m.name : `${ m.name } (inactive)`,
			description: m.provider,
		} ) ), [ data.models, draft?.defaultAIModelId ] );

	const instructionOptions: DropdownItemProps[] = useMemo( () => data.instructions
		.filter( i => i.active || draft?.instructionIds.includes( i.id ) )
		.map( i => ( {
			key:         i.id,
			value:       i.id,
			text:        i.active ? i.name : `${ i.name } (inactive)`,
		} ) ), [ data.instructions, draft?.instructionIds ] );

	const anchorOptions: DropdownItemProps[] = useMemo( () => data.anchors
		.filter( a => a.active || draft?.anchorIds.includes( a.id ) )
		.map( a => ( {
			key:         a.id,
			value:       a.id,
			text:        a.active ? a.name : `${ a.name } (inactive)`,
		} ) ), [ data.anchors, draft?.anchorIds ] );

	const toolOptions: DropdownItemProps[] = useMemo( () => data.tools
		.filter( t => t.active || draft?.toolIds.includes( t.id ) )
		.map( t => ( {
			key:         t.id,
			value:       t.id,
			text:        t.active ? t.name : `${ t.name } (inactive)`,
			description: t.toolType ?? undefined,
		} ) ), [ data.tools, draft?.toolIds ] );

	// ── List ───────────────────────────────────────────────────────────────────

	const listRows: ListRow[] = useMemo( () => {
		const plural = ( count: number, word: string ) => `${ count } ${ word }${ count === 1 ? '' : 's' }`;
		const rows: ListRow[] =
			tab === 'agent' ? data.agents.map( a => ( {
				id:     a.id,
				name:   a.name,
				active: a.active,
				meta:   [ a.defaultAIModelName, plural( a.instructionIds.length, 'instruction' ), plural( a.anchorIds.length, 'anchor' ), plural( a.toolIds.length, 'tool' ) ]
					.filter( Boolean ).join( ' · ' ),
			} ) )
			: tab === 'instruction' || tab === 'anchor' ? ( tab === 'anchor' ? data.anchors : data.instructions ).map( i => ( {
				id:     i.id,
				name:   i.name,
				active: i.active,
				meta:   `used by ${ plural( i.agentCount, 'agent' ) }`,
			} ) )
			: data.tools.map( t => ( {
				id:     t.id,
				name:   t.name,
				active: t.active,
				meta:   [ t.toolType, `used by ${ plural( t.agentCount, 'agent' ) }` ].filter( Boolean ).join( ' · ' ),
			} ) );

		const term = search.trim().toLowerCase();
		return term ? rows.filter( r => r.name.toLowerCase().includes( term ) || r.meta.toLowerCase().includes( term ) ) : rows;
	}, [ tab, data, search ] );

	// ── Editor ─────────────────────────────────────────────────────────────────

	const usageCount = !draft?.id ? 0
		: tab === 'instruction' ? data.instructions.find( i => i.id === draft.id )?.agentCount ?? 0
		: tab === 'anchor' ? data.anchors.find( a => a.id === draft.id )?.agentCount ?? 0
		: tab === 'tool' ? data.tools.find( t => t.id === draft.id )?.agentCount ?? 0
		: 0;

	const renderEditor = () => {
		if( !draft ){
			return (
				<div className={ 'ai-mgmt-empty' }>
					<Icon name={ TAB_LABELS[ tab ].icon } size={ 'large' }/>
					<span>Select an item on the left, or create a new { TAB_LABELS[ tab ].singular.toLowerCase() }.</span>
					{ canCreate
						? <Button basic size={ 'small' } icon={ 'plus' } content={ `New ${ TAB_LABELS[ tab ].singular }` } onClick={ handleNew }/>
						: <span className={ 'ai-mgmt-list-meta' }>{ scopeError }</span> }
				</div>
			);
		}

		const label = TAB_LABELS[ tab ].singular;

		return (
			<Form className={ 'ai-mgmt-form' } onSubmit={ handleSave }>
				<div className={ 'ai-mgmt-form-header' }>
					<span className={ 'ai-mgmt-form-title' }>{ isNew ? `New ${ label.toLowerCase() }` : draft.name || label }</span>
					<Checkbox
						toggle
						label={ draft.active ? 'Active' : 'Inactive' }
						checked={ draft.active }
						disabled={ isSaving }
						onChange={ ( _e, d ) => updateDraft( { active: !!d.checked } ) }
					/>
				</div>

				{ formError && (
					<p className={ 'create-client-error' }>
						<Icon name={ 'warning circle' as SemanticICONS }/> { formError }
					</p>
				) }

				{ tab === 'agent' && activeModels.length === 0 && (
					<Message warning size={ 'small' } content={ 'No active AI models are set up yet. An agent needs a default model before it can be saved.' }/>
				) }

				<Form.Input
					label={ 'Name' }
					value={ draft.name }
					maxLength={ MAX_NAME_LENGTH }
					placeholder={ tab === 'agent' ? 'e.g. Research Agent'
						: tab === 'instruction' ? 'e.g. Cite sources'
						: tab === 'anchor' ? 'e.g. Company background'
						: 'e.g. Web Search' }
					disabled={ isSaving }
					onChange={ ( _e, d ) => updateDraft( { name: String( d.value ?? '' ) } ) }
				/>

				{ tab === 'instruction' && (
					<Form.TextArea
						label={ 'Instructions' }
						value={ draft.instructions }
						rows={ 8 }
						placeholder={ 'What the agent should do or follow, e.g. "Always cite sources when available."' }
						disabled={ isSaving }
						onChange={ ( _e, d ) => updateDraft( { instructions: String( d.value ?? '' ) } ) }
					/>
				) }

				{ tab === 'anchor' && (
					<Form.TextArea
						label={ 'Anchor' }
						value={ draft.instructions }
						rows={ 8 }
						placeholder={ 'The anchor text the agent should be given' }
						disabled={ isSaving }
						onChange={ ( _e, d ) => updateDraft( { instructions: String( d.value ?? '' ) } ) }
					/>
				) }

				{ tab === 'tool' && (
					<Form.Input
						label={ 'Tool type (optional)' }
						value={ draft.toolType }
						maxLength={ MAX_NAME_LENGTH }
						placeholder={ 'e.g. search, retrieval, function' }
						disabled={ isSaving }
						onChange={ ( _e, d ) => updateDraft( { toolType: String( d.value ?? '' ) } ) }
					/>
				) }

				<Form.TextArea
					label={ 'Description (optional)' }
					value={ draft.description }
					rows={ 2 }
					placeholder={ tab === 'agent' ? 'What does this agent help with?' : 'Notes for other admins' }
					disabled={ isSaving }
					onChange={ ( _e, d ) => updateDraft( { description: String( d.value ?? '' ) } ) }
				/>

				{ tab === 'agent' && (
					<>
						<Form.Dropdown
							label={ 'Default model' }
							selection
							search
							options={ modelOptions }
							value={ draft.defaultAIModelId }
							placeholder={ 'Select a model' }
							disabled={ isSaving }
							onChange={ ( _e, d ) => updateDraft( { defaultAIModelId: String( d.value ?? '' ) } ) }
						/>

						<Form.Dropdown
							label={ 'Instructions' }
							selection
							search
							multiple
							options={ instructionOptions }
							value={ draft.instructionIds }
							placeholder={ instructionOptions.length ? 'Add instructions' : 'No instructions yet — create them on the Instructions tab' }
							disabled={ isSaving }
							onChange={ ( _e, d ) => updateDraft( { instructionIds: ( d.value as string[] ) ?? [] } ) }
						/>

						<Form.Dropdown
							label={ 'Anchors' }
							selection
							search
							multiple
							options={ anchorOptions }
							value={ draft.anchorIds }
							placeholder={ anchorOptions.length ? 'Add anchors' : 'No anchors yet — create them on the Anchors tab' }
							disabled={ isSaving }
							onChange={ ( _e, d ) => updateDraft( { anchorIds: ( d.value as string[] ) ?? [] } ) }
						/>

						<Form.Dropdown
							label={ 'Tools' }
							selection
							search
							multiple
							options={ toolOptions }
							value={ draft.toolIds }
							placeholder={ toolOptions.length ? 'Add tools' : 'No tools yet — create them on the Tools tab' }
							disabled={ isSaving }
							onChange={ ( _e, d ) => updateDraft( { toolIds: ( d.value as string[] ) ?? [] } ) }
						/>
					</>
				) }

				{ usageCount > 0 && (
					<p className={ 'ai-mgmt-usage' }>
						<Icon name={ 'info circle' as SemanticICONS }/>
						Used by { usageCount } agent{ usageCount === 1 ? '' : 's' }. Changes apply to all of them.
					</p>
				) }

				<div className={ 'ai-mgmt-form-actions' }>
					<Button
						type={ 'button' }
						basic
						content={ 'Discard changes' }
						disabled={ isSaving || !isDirty }
						onClick={ handleReset }
					/>
					<Button
						type={ 'submit' }
						color={ 'purple' }
						loading={ isSaving }
						disabled={ isSaving || ( !isNew && !isDirty ) }
						content={ isNew ? `Create ${ label.toLowerCase() }` : 'Save changes' }
					/>
				</div>
			</Form>
		);
	};

	// ── Render ─────────────────────────────────────────────────────────────────

	const counts: Record<AIManagementTab, number> = {
		agent:       data.agents.length,
		instruction: data.instructions.length,
		anchor:      data.anchors.length,
		tool:        data.tools.length,
	};

	return (
		<BasicDialog
			showCancelButton
			showSubmitButton={ false }
			cancelLabel={ 'Close' }
			autoClose={ false }
			onClose={ handleClose }
			controlAlign={ Align.RIGHT }
			className={ 'no-padding create-client-dialog ai-mgmt-dialog' }
		>
			<BasicDialogContainer
				title={ 'AI Agent Management' }
				titleColor={ SemanticUIColor.PURPLE }
				contentPadding={ null }
				onClose={ handleClose }
			>
				<Menu secondary pointing className={ 'ai-mgmt-tabs' }>
					{ ( Object.keys( TAB_LABELS ) as AIManagementTab[] ).map( key => (
						<Menu.Item
							key={ key }
							active={ tab === key }
							disabled={ isSaving }
							onClick={ () => handleTabChange( key ) }
						>
							<Icon name={ TAB_LABELS[ key ].icon }/>
							{ TAB_LABELS[ key ].plural }
							{ !isLoading && <Label size={ 'tiny' } circular>{ counts[ key ] }</Label> }
						</Menu.Item>
					) ) }
				</Menu>

				{ isLoading && !data.agents.length && !data.instructions.length && !data.anchors.length && !data.tools.length ? (
					<div className={ 'ai-mgmt-loading' }>
						<Loader active inline={ 'centered' } content={ 'Loading…' }/>
					</div>
				) : loadError ? (
					<div className={ 'ai-mgmt-loading' }>
						<Message negative size={ 'small' }>
							<p>{ loadError }</p>
							<Button basic size={ 'mini' } icon={ 'redo' } content={ 'Try again' } onClick={ () => loadData() }/>
						</Message>
					</div>
				) : (
					<div className={ 'ai-mgmt-body' }>
						<div className={ 'ai-mgmt-list-pane' }>
							<div className={ 'ai-mgmt-list-toolbar' }>
								<Input
									icon={ 'search' }
									size={ 'small' }
									placeholder={ `Search ${ TAB_LABELS[ tab ].plural.toLowerCase() }` }
									value={ search }
									onChange={ ( _e, d ) => setSearch( String( d.value ?? '' ) ) }
								/>
								<Button
									icon={ 'plus' }
									size={ 'small' }
									basic
									title={ canCreate ? `New ${ TAB_LABELS[ tab ].singular.toLowerCase() }` : scopeError }
									disabled={ isSaving || !canCreate }
									onClick={ handleNew }
								/>
							</div>

							<div className={ 'ai-mgmt-list' } role={ 'listbox' }>
								{ isNew && (
									<div className={ 'ai-mgmt-list-item active is-new' }>
										<span className={ 'ai-mgmt-list-name' }>New { TAB_LABELS[ tab ].singular.toLowerCase() }</span>
										<span className={ 'ai-mgmt-list-meta' }>Not saved yet</span>
									</div>
								) }
								{ listRows.map( row => (
									<button
										key={ row.id }
										type={ 'button' }
										role={ 'option' }
										aria-selected={ row.id === selectedId }
										className={ `ai-mgmt-list-item${ row.id === selectedId ? ' active' : '' }${ row.active ? '' : ' is-inactive' }` }
										disabled={ isSaving }
										onClick={ () => handleSelect( row.id ) }
									>
										<span className={ 'ai-mgmt-list-name' }>
											{ row.name }
											{ !row.active && <Label size={ 'mini' } basic>Inactive</Label> }
										</span>
										<span className={ 'ai-mgmt-list-meta' }>{ row.meta }</span>
									</button>
								) ) }
								{ listRows.length === 0 && !isNew && (
									<span className={ 'ai-mgmt-list-empty' }>
										{ search ? 'No matches.'
											: tab !== 'tool' && !clientId ? `No client selected, so there are no ${ TAB_LABELS[ tab ].plural.toLowerCase() } to show. Open this from a client on the dashboard.`
											: `No ${ TAB_LABELS[ tab ].plural.toLowerCase() } yet.` }
									</span>
								) }
							</div>
						</div>

						<div className={ 'ai-mgmt-editor-pane' }>
							{ renderEditor() }
						</div>
					</div>
				) }
			</BasicDialogContainer>
		</BasicDialog>
	);
};

export default AIAgentManagement;
